import React, { useState, useEffect, useRef } from "react";
import { useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  setPickup,
  setDropoff,
  setDistance,
  setDuration,
  setServiceType,
  setPassenger,
  setLuggage,
  setBooster,
  setRegular,
  setInfant,
  setDate,
  setTime,
} from "../features/bookingSlice/bookingSlice";
import MobileDatePickerModal from "../components/MobileDatePickerModal";

const MAP_LIBRARIES = ['places'];

const FirstStep = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: MAP_LIBRARIES,
  });

  const fixedInputValue = "Boston Logan International Airport (BOS)";
  const fixedCoordinates = { lat: 42.3656338, lng: -71.0282381 };

  const selected = useSelector((state) => state.booking.service_type) || "to_airport";
  const { passenger, luggage, booster, regular, infant, date, time, pickup, dropoff } = useSelector((state) => state.booking);
  
  const [showChildSeat, setShowChildSeat] = useState(Boolean(booster > 0 || regular > 0 || infant > 0));
  
  const [localDate, setLocalDate] = useState(date ? new Date(date).toISOString().split('T')[0] : "");
  const [localTime, setLocalTime] = useState(time || "");
  const [isMobileDatePickerOpen, setIsMobileDatePickerOpen] = useState(false);

  const autocompletePickup = useRef(null);
  const autocompleteDropoff = useRef(null);
  const dateInputRef = useRef(null);

  // Generate 15-minute interval time slots in 12-hour format
  const generateTimeSlots = () => {
    const slots = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        const period = h >= 12 ? 'pm' : 'am';
        const displayH = h % 12 || 12;
        const displayM = m.toString().padStart(2, '0');
        const formatted = `${displayH}:${displayM}${period}`;
        slots.push({
          value: formatted,
          label: `${displayH}:${displayM} ${period.toUpperCase()}`
        });
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const handleChangeService = (type) => {
    dispatch(setServiceType(type));
    if (type === "from_airport") {
      dispatch(setDropoff({ address: "", coordinates: null }));
      dispatch(setPickup({ address: fixedInputValue, coordinates: fixedCoordinates }));
    } else if (type === "to_airport") {
      dispatch(setPickup({ address: "", coordinates: null }));
      dispatch(setDropoff({ address: fixedInputValue, coordinates: fixedCoordinates }));
    } else {
      dispatch(setPickup({ address: "", coordinates: null }));
      dispatch(setDropoff({ address: "", coordinates: null }));
    }
  };

  const handleSwapLocations = (e) => {
    e.preventDefault();
    if (selected !== 'point_to_point' && selected !== 'long_distance') return;
    const tempPickup = pickup;
    dispatch(setPickup(dropoff));
    dispatch(setDropoff(tempPickup));
  };

  const handleDateChange = (e) => {
    setLocalDate(e.target.value);
    if(e.target.value) {
      const [year, month, day] = e.target.value.split('-');
      dispatch(setDate(`${month}/${day}/${year}`));
    }
  };

  const handleSelectDate = (dateString) => {
    setLocalDate(dateString);
    if (dateString) {
      const [year, month, day] = dateString.split('-');
      dispatch(setDate(`${month}/${day}/${year}`));
    }
  };

  const handleTimeChange = (val) => {
    setLocalTime(val);
    if (val) {
      dispatch(setTime(val));
    }
  };

  const handlePlaceChanged = (ref, actionCreator) => {
    if (ref.current) {
      const place = ref.current.getPlace();
      if (place && place.formatted_address && place.geometry) {
        const location = place.geometry.location;
        dispatch(actionCreator({
          address: place.formatted_address,
          coordinates: { lat: location.lat(), lng: location.lng() },
        }));
      }
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") event.preventDefault();
  };

  useEffect(() => {
    if (pickup?.coordinates && dropoff?.coordinates && isLoaded) {
      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route({
        origin: pickup.coordinates,
        destination: dropoff.coordinates,
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.IMPERIAL,
        region: 'US'
      }, (result, status) => {
        if (status === "OK") {
          dispatch(setDistance(result.routes[0].legs[0].distance.text));
          dispatch(setDuration(result.routes[0].legs[0].duration.text));
        }
      });
    }
  }, [pickup, dropoff, isLoaded, dispatch]);

  const handleQuoteClick = (e) => {
    e.preventDefault();
    if (!pickup?.address || !dropoff?.address) {
      alert("Please enter both pickup and dropoff locations to proceed.");
      return;
    }
    navigate("/vehicleselection");
  };

  return (
    <div className="relative min-h-screen bg-[#0b132b] text-slate-100 flex flex-col justify-between">
      
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-12 pb-20 lg:pt-16 lg:pb-28">
        {/* Background Overlay */}
        <div 
          className="absolute inset-0 z-0 bg-cover bg-center filter brightness-[0.25] contrast-125 scale-105 transition-transform duration-1000" 
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1549399542-7e3f8b79c341?auto=format&fit=crop&w=2200&q=85')" }}
        />
        
        {/* Vignette & Blue Radiance */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b132b] via-[#0b132b]/85 to-[#0b132b]/95 z-0" />
        <div className="absolute top-0 inset-x-0 h-40 bg-gradient-to-b from-[#0b132b] to-transparent z-0" />
        
        {/* Ambient Blue Backlights */}
        <div className="absolute top-12 left-1/4 w-[500px] h-[500px] bg-blue-600/15 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-12 right-12 w-[550px] h-[550px] bg-sky-500/10 rounded-full blur-[160px] pointer-events-none" />

        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {/* Left Column: Corporate Value Proposition */}
          <div className="lg:col-span-6 space-y-8 text-center lg:text-left">
            
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-slate-900/90 border border-blue-500/40 text-blue-300 text-xs font-bold tracking-widest uppercase shadow-xl backdrop-blur-xl">
              <span className="flex h-2.5 w-2.5 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500"></span>
              </span>
              Premier Corporate Airport Shuttle & Chauffeur
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tight text-white leading-[1.08]">
              Executive Travel. <br />
              <span className="blue-gradient-text drop-shadow-sm">Seamless Arrivals.</span>
            </h1>

            <p className="text-slate-300 text-base sm:text-lg lg:text-xl font-normal leading-relaxed max-w-xl mx-auto lg:mx-0">
              Corporate sedans, business-class SUVs, and private airport transfers serving Greater Boston, Logan International (BOS), and New England.
            </p>

            {/* Corporate Trust Badges */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-8 pt-2">
              <div className="flex items-center gap-3">
                <div className="flex text-amber-400 text-base">
                  {"★★★★★"}
                </div>
                <div>
                  <div className="text-white font-extrabold text-sm">4.9 / 5.0 Rating</div>
                  <div className="text-slate-400 text-xs">Over 25,000+ Corporate Transfers</div>
                </div>
              </div>
              <div className="h-8 w-px bg-slate-700/60 hidden sm:block"></div>
              <div className="flex items-center gap-2 text-xs text-slate-300">
                <span className="material-symbols-outlined text-blue-400 !text-xl">flight_takeoff</span>
                <span>Live Flight Radar Sync</span>
              </div>
            </div>

            {/* Value Pillars */}
            <div className="grid grid-cols-3 gap-3.5 pt-2 max-w-lg mx-auto lg:mx-0">
              <div className="p-4 rounded-2xl bg-slate-900/70 border border-slate-700/60 backdrop-blur-xl hover:border-blue-500/50 transition-all text-left group">
                <span className="material-symbols-outlined text-blue-400 mb-2 !text-2xl group-hover:scale-110 transition-transform">schedule</span>
                <h4 className="text-white font-bold text-xs sm:text-sm">Guaranteed Punctual</h4>
                <p className="text-slate-400 text-[11px] mt-0.5">15 min early arrival</p>
              </div>
              <div className="p-4 rounded-2xl bg-slate-900/70 border border-slate-700/60 backdrop-blur-xl hover:border-blue-500/50 transition-all text-left group">
                <span className="material-symbols-outlined text-blue-400 mb-2 !text-2xl group-hover:scale-110 transition-transform">verified_user</span>
                <h4 className="text-white font-bold text-xs sm:text-sm">Licensed & Insured</h4>
                <p className="text-slate-400 text-[11px] mt-0.5">MassDOT & FAA compliant</p>
              </div>
              <div className="p-4 rounded-2xl bg-slate-900/70 border border-slate-700/60 backdrop-blur-xl hover:border-blue-500/50 transition-all text-left group">
                <span className="material-symbols-outlined text-blue-400 mb-2 !text-2xl group-hover:scale-110 transition-transform">receipt_long</span>
                <h4 className="text-white font-bold text-xs sm:text-sm">Transparent Invoicing</h4>
                <p className="text-slate-400 text-[11px] mt-0.5">Corporate billing ready</p>
              </div>
            </div>
          </div>
          
          {/* Right Column: Executive Navy & Silver Booking Widget */}
          <div className="lg:col-span-6 w-full max-w-xl mx-auto">
            <div className="relative rounded-[28px] bg-slate-900/90 border border-slate-700/70 p-6 sm:p-8 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.8)] backdrop-blur-2xl ring-1 ring-white/10">
              
              {/* Header inside widget */}
              <div className="flex items-center justify-between border-b border-slate-800 pb-5 mb-6">
                <div>
                  <h3 className="text-xl font-bold text-white tracking-tight">Reserve Executive Chauffeur</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Direct airport transfer & point-to-point dispatch</p>
                </div>
                <div className="flex items-center gap-1.5 text-[11px] font-bold px-3 py-1 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/30">
                  <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                  Fleet Active
                </div>
              </div>

              {/* Service Type Switcher */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 p-1.5 bg-slate-950/90 rounded-2xl border border-slate-800 mb-6">
                <button 
                  type="button"
                  className={`py-3 px-2 text-xs font-bold rounded-xl transition-all duration-200 flex items-center justify-center gap-1.5 ${
                    selected === 'to_airport' 
                      ? 'bg-blue-600 text-white font-black shadow-lg shadow-blue-600/30 scale-[1.02]' 
                      : 'text-slate-300 hover:text-white hover:bg-white/5'
                  }`} 
                  onClick={() => handleChangeService('to_airport')}
                >
                  <span className="material-symbols-outlined !text-sm">flight_takeoff</span>
                  To Logan
                </button>
                <button 
                  type="button"
                  className={`py-3 px-2 text-xs font-bold rounded-xl transition-all duration-200 flex items-center justify-center gap-1.5 ${
                    selected === 'from_airport' 
                      ? 'bg-blue-600 text-white font-black shadow-lg shadow-blue-600/30 scale-[1.02]' 
                      : 'text-slate-300 hover:text-white hover:bg-white/5'
                  }`} 
                  onClick={() => handleChangeService('from_airport')}
                >
                  <span className="material-symbols-outlined !text-sm">flight_land</span>
                  From Logan
                </button>
                <button 
                  type="button"
                  className={`py-3 px-2 text-xs font-bold rounded-xl transition-all duration-200 flex items-center justify-center gap-1.5 ${
                    selected === 'point_to_point' 
                      ? 'bg-blue-600 text-white font-black shadow-lg shadow-blue-600/30 scale-[1.02]' 
                      : 'text-slate-300 hover:text-white hover:bg-white/5'
                  }`} 
                  onClick={() => handleChangeService('point_to_point')}
                >
                  <span className="material-symbols-outlined !text-sm">swap_horiz</span>
                  Point-to-Point
                </button>
                <button 
                  type="button"
                  className={`py-3 px-2 text-xs font-bold rounded-xl transition-all duration-200 flex items-center justify-center gap-1.5 ${
                    selected === 'long_distance' 
                      ? 'bg-blue-600 text-white font-black shadow-lg shadow-blue-600/30 scale-[1.02]' 
                      : 'text-slate-300 hover:text-white hover:bg-white/5'
                  }`} 
                  onClick={() => handleChangeService('long_distance')}
                >
                  <span className="material-symbols-outlined !text-sm">distance</span>
                  Long Distance
                </button>
              </div>

              {/* Form Controls */}
              <form onSubmit={handleQuoteClick} className="space-y-4">
                
                {/* Pickup & Dropoff Inputs */}
                <div className="relative space-y-3">
                  
                  {/* Pickup Location */}
                  <div className="relative group">
                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-blue-400" />
                      Pickup Address / Terminal
                    </label>
                    <div className="relative">
                      {isLoaded ? (
                        <Autocomplete
                          options={{
                            componentRestrictions: { country: ["us"] },
                            fields: ["formatted_address", "geometry", "name"]
                          }}
                          onLoad={(autocomplete) => (autocompletePickup.current = autocomplete)}
                          onPlaceChanged={() => handlePlaceChanged(autocompletePickup, setPickup)}
                        >
                          <input
                            type="text"
                            placeholder="Enter hotel, office, street or airport..."
                            value={pickup?.address || ""}
                            onChange={(e) => dispatch(setPickup({ address: e.target.value, coordinates: null }))}
                            onKeyDown={handleKeyDown}
                            disabled={selected === "from_airport"}
                            className="w-full bg-slate-950/80 border border-slate-700/80 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 rounded-xl px-4 py-3.5 pl-11 text-sm text-white placeholder:text-slate-500 outline-none transition-all"
                            required
                          />
                        </Autocomplete>
                      ) : (
                        <input
                          type="text"
                          placeholder="Loading Google Places..."
                          disabled
                          className="w-full bg-slate-950/80 border border-slate-700 rounded-xl px-4 py-3.5 pl-11 text-sm text-slate-400"
                        />
                      )}
                      <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-blue-400 !text-xl pointer-events-none">
                        location_on
                      </span>
                    </div>
                  </div>

                  {/* Swap button (P2P only) */}
                  {(selected === 'point_to_point' || selected === 'long_distance') && (
                    <div className="flex justify-end -my-1 pr-3">
                      <button 
                        type="button" 
                        onClick={handleSwapLocations}
                        className="w-8 h-8 rounded-full bg-slate-800 border border-slate-700 hover:border-blue-400 text-slate-300 hover:text-white flex items-center justify-center transition-all shadow-md"
                        title="Swap addresses"
                      >
                        <span className="material-symbols-outlined !text-base">swap_vert</span>
                      </button>
                    </div>
                  )}

                  {/* Dropoff Location */}
                  <div className="relative group">
                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-sky-400" />
                      Destination Address
                    </label>
                    <div className="relative">
                      {isLoaded ? (
                        <Autocomplete
                          options={{
                            componentRestrictions: { country: ["us"] },
                            fields: ["formatted_address", "geometry", "name"]
                          }}
                          onLoad={(autocomplete) => (autocompleteDropoff.current = autocomplete)}
                          onPlaceChanged={() => handlePlaceChanged(autocompleteDropoff, setDropoff)}
                        >
                          <input
                            type="text"
                            placeholder="Enter dropoff location or terminal..."
                            value={dropoff?.address || ""}
                            onChange={(e) => dispatch(setDropoff({ address: e.target.value, coordinates: null }))}
                            onKeyDown={handleKeyDown}
                            disabled={selected === "to_airport"}
                            className="w-full bg-slate-950/80 border border-slate-700/80 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 rounded-xl px-4 py-3.5 pl-11 text-sm text-white placeholder:text-slate-500 outline-none transition-all"
                            required
                          />
                        </Autocomplete>
                      ) : (
                        <input
                          type="text"
                          placeholder="Loading Google Places..."
                          disabled
                          className="w-full bg-slate-950/80 border border-slate-700 rounded-xl px-4 py-3.5 pl-11 text-sm text-slate-400"
                        />
                      )}
                      <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-sky-400 !text-xl pointer-events-none">
                        flag
                      </span>
                    </div>
                  </div>
                </div>

                {/* Date & Time Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-1">
                  
                  {/* Date Input */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                      <span className="material-symbols-outlined text-blue-400 !text-sm">calendar_today</span>
                      Pickup Date
                    </label>
                    
                    {/* Desktop Datepicker (Current native input preserved for desktop) */}
                    <div 
                      className="hidden sm:block relative cursor-pointer"
                      onClick={() => {
                        if (dateInputRef.current) {
                          if (dateInputRef.current.showPicker) {
                            dateInputRef.current.showPicker();
                          } else {
                            dateInputRef.current.focus();
                          }
                        }
                      }}
                    >
                      <input
                        ref={dateInputRef}
                        type="date"
                        value={localDate}
                        onChange={handleDateChange}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full bg-slate-950/80 border border-slate-700/80 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 rounded-xl px-4 py-3 text-sm text-white outline-none transition-all scheme-dark cursor-pointer"
                        required
                      />
                    </div>

                    {/* Mobile Datepicker Button (Triggers modern executive calendar popup on mobile) */}
                    <div className="block sm:hidden">
                      <button
                        type="button"
                        onClick={() => setIsMobileDatePickerOpen(true)}
                        className="w-full bg-slate-950/80 border border-slate-700/80 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 rounded-xl px-4 py-3 text-sm text-white outline-none transition-all flex items-center justify-between text-left"
                      >
                        <span className={localDate ? "text-white font-mono" : "text-slate-400"}>
                          {localDate ? (
                            (() => {
                              const [y, m, d] = localDate.split('-');
                              const dt = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
                              return dt.toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              });
                            })()
                          ) : (
                            "Select pickup date"
                          )}
                        </span>
                        <span className="material-symbols-outlined text-blue-400 !text-lg">
                          calendar_month
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Time Input (15-min Intervals in 12-hr Format) */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                      <span className="material-symbols-outlined text-blue-400 !text-sm">schedule</span>
                      Pickup Time (15-min Interval)
                    </label>
                    <div className="relative">
                      <select
                        value={localTime}
                        onChange={(e) => handleTimeChange(e.target.value)}
                        className="w-full bg-slate-950/80 border border-slate-700/80 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 rounded-xl px-4 py-3 text-sm text-white outline-none transition-all cursor-pointer appearance-none"
                        required
                      >
                        <option value="" disabled className="bg-slate-900 text-slate-400">Select pickup time</option>
                        {timeSlots.map((slot) => (
                          <option key={slot.value} value={slot.value} className="bg-slate-900 text-white py-1">
                            {slot.label}
                          </option>
                        ))}
                      </select>
                      <span className="material-symbols-outlined absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 !text-base pointer-events-none">
                        expand_more
                      </span>
                    </div>
                  </div>

                </div>

                {/* Passengers & Luggage Controls */}
                <div className="grid grid-cols-2 gap-3.5 pt-1">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                      Passengers
                    </label>
                    <div className="flex items-center justify-between bg-slate-950/80 border border-slate-700/80 rounded-xl px-3 py-2">
                      <button 
                        type="button" 
                        onClick={() => dispatch(setPassenger(Math.max(1, (passenger || 1) - 1)))}
                        className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-700 hover:border-blue-400 text-white font-bold flex items-center justify-center transition-all"
                      >
                        -
                      </button>
                      <span className="text-sm font-extrabold text-white font-mono">{passenger || 1} Guests</span>
                      <button 
                        type="button" 
                        onClick={() => dispatch(setPassenger(Math.min(14, (passenger || 1) + 1)))}
                        className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-700 hover:border-blue-400 text-white font-bold flex items-center justify-center transition-all"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                      Luggage Capacity
                    </label>
                    <div className="flex items-center justify-between bg-slate-950/80 border border-slate-700/80 rounded-xl px-3 py-2">
                      <button 
                        type="button" 
                        onClick={() => dispatch(setLuggage(Math.max(0, (luggage || 0) - 1)))}
                        className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-700 hover:border-blue-400 text-white font-bold flex items-center justify-center transition-all"
                      >
                        -
                      </button>
                      <span className="text-sm font-extrabold text-white font-mono">{luggage || 0} Bags</span>
                      <button 
                        type="button" 
                        onClick={() => dispatch(setLuggage(Math.min(14, (luggage || 0) + 1)))}
                        className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-700 hover:border-blue-400 text-white font-bold flex items-center justify-center transition-all"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>

                {/* Add Child Seat Section */}
                <div className="pt-2 border-t border-slate-800">
                  <div className="flex items-center justify-between py-1">
                    <span className="text-sm font-bold text-white flex items-center gap-2">
                      <span className="material-symbols-outlined text-emerald-400 !text-lg">child_care</span>
                      Add Child Seat
                    </span>
                    
                    {/* Toggle Switch */}
                    <button
                      type="button"
                      onClick={() => {
                        const next = !showChildSeat;
                        setShowChildSeat(next);
                        if (!next) {
                          dispatch(setBooster(0));
                          dispatch(setRegular(0));
                          dispatch(setInfant(0));
                        }
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                        showChildSeat ? 'bg-emerald-600' : 'bg-slate-700'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          showChildSeat ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Child Seat Dropdowns (Booster, Regular, Infant) */}
                  {showChildSeat && (
                    <div className="grid grid-cols-3 gap-2.5 pt-3 animate-fadeIn">
                      {/* Booster */}
                      <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                          Booster
                        </label>
                        <div className="relative">
                          <select
                            value={booster || 0}
                            onChange={(e) => dispatch(setBooster(Number(e.target.value)))}
                            className="w-full bg-slate-950/80 border border-slate-700/80 rounded-xl px-2.5 py-2.5 text-xs text-white outline-none focus:border-blue-500 appearance-none cursor-pointer"
                          >
                            {[0, 1, 2, 3, 4].map((num) => (
                              <option key={num} value={num} className="bg-slate-900 text-white">
                                {num === 0 ? "0 Booster" : `${num} Booster`}
                              </option>
                            ))}
                          </select>
                          <span className="material-symbols-outlined absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 !text-sm pointer-events-none">
                            expand_more
                          </span>
                        </div>
                      </div>

                      {/* Regular (Forward/Rear facing toddler seat) */}
                      <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                          Regular
                        </label>
                        <div className="relative">
                          <select
                            value={regular || 0}
                            onChange={(e) => dispatch(setRegular(Number(e.target.value)))}
                            className="w-full bg-slate-950/80 border border-slate-700/80 rounded-xl px-2.5 py-2.5 text-xs text-white outline-none focus:border-blue-500 appearance-none cursor-pointer"
                          >
                            {[0, 1, 2, 3, 4].map((num) => (
                              <option key={num} value={num} className="bg-slate-900 text-white">
                                {num === 0 ? "0 Regular" : `${num} Regular`}
                              </option>
                            ))}
                          </select>
                          <span className="material-symbols-outlined absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 !text-sm pointer-events-none">
                            expand_more
                          </span>
                        </div>
                      </div>

                      {/* Infant */}
                      <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                          Infant
                        </label>
                        <div className="relative">
                          <select
                            value={infant || 0}
                            onChange={(e) => dispatch(setInfant(Number(e.target.value)))}
                            className="w-full bg-slate-950/80 border border-slate-700/80 rounded-xl px-2.5 py-2.5 text-xs text-white outline-none focus:border-blue-500 appearance-none cursor-pointer"
                          >
                            {[0, 1, 2, 3, 4].map((num) => (
                              <option key={num} value={num} className="bg-slate-900 text-white">
                                {num === 0 ? "0 Infant" : `${num} Infant`}
                              </option>
                            ))}
                          </select>
                          <span className="material-symbols-outlined absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 !text-sm pointer-events-none">
                            expand_more
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Submit / Continue Button */}
                <button
                  type="submit"
                  className="w-full mt-6 py-4 rounded-xl exec-btn-primary flex items-center justify-center gap-2 text-xs uppercase tracking-wider font-extrabold shadow-xl"
                >
                  <span className="material-symbols-outlined !text-lg">directions_car</span>
                  View Available Vehicles & Rates
                  <span className="material-symbols-outlined !text-base">arrow_forward</span>
                </button>
              </form>

              {/* Trust Footer */}
              <div className="mt-5 pt-4 border-t border-slate-800 flex items-center justify-between text-[11px] text-slate-400">
                <span className="flex items-center gap-1">
                  <span className="material-symbols-outlined text-emerald-400 !text-sm">verified</span>
                  Complimentary Flight Tracking
                </span>
                <span className="flex items-center gap-1">
                  <span className="material-symbols-outlined text-blue-400 !text-sm">security</span>
                  Fixed Pricing Guarantee
                </span>
              </div>

            </div>
          </div>

        </div>
      </section>

      {/* Modern Mobile-Only Calendar Bottom-Sheet Popup */}
      <MobileDatePickerModal
        isOpen={isMobileDatePickerOpen}
        onClose={() => setIsMobileDatePickerOpen(false)}
        selectedDate={localDate}
        onSelectDate={handleSelectDate}
        minDate={new Date().toISOString().split('T')[0]}
      />

    </div>
  );
};

export default FirstStep;
